import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import {
  CheckCircle,
  X as XIcon,
  Zap,
  Shield,
  Server,
  Clock,
  Mail,
  Star,
  ArrowRight,
  HelpCircle,
  MessageCircle,
  Crown,
  BookOpen
} from 'lucide-react';
import * as Icons from 'lucide-react';
import { API_URLS } from '../../config/api';
import MediaDisplay from '../MediaDisplay';

const resolveIcon = (iconName, fallback = 'CheckCircle') => {
  if (!iconName && Icons[fallback]) return Icons[fallback];
  if (iconName && Icons[iconName]) return Icons[iconName];
  if (fallback && Icons[fallback]) return Icons[fallback];
  return CheckCircle;
};

const WordPressHostingPage = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [plansError, setPlansError] = useState('');
  const [faqs, setFaqs] = useState([]);
  const [loadingFaqs, setLoadingFaqs] = useState(true);
  const [faqsError, setFaqsError] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('905324404011');
  const [expandedFeatures, setExpandedFeatures] = useState({});
  const [contentReady, setContentReady] = useState(false);
  const [pageContent, setPageContent] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setContentReady(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const toggleFeatures = (planId) => {
    setExpandedFeatures((prev) => ({
      ...prev,
      [planId]: !prev[planId]
    }));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPlans();
    fetchFaqs();
    fetchSettings();
    fetchContent();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoadingPlans(true);
      const res = await fetch(API_URLS.HOSTING_PACKAGES);
      const json = await res.json();
      const dataArray = Array.isArray(json)
        ? json
        : json && json.status === 'success' && Array.isArray(json.data)
        ? json.data
        : [];
      if (Array.isArray(dataArray)) {
        const wp = dataArray.filter((p) => {
          const t = (p.type || '').toString().toLowerCase();
          const n = (p.name || '').toString().toLowerCase();
          return t.includes('wordpress') || n.includes('wordpress') || /\bwp\b/.test(n);
        });
        const mapped = wp.map((p, idx) => {
          let featuresArr = [];
          if (Array.isArray(p.features)) {
            featuresArr = p.features;
          } else if (typeof p.features === 'string' && p.features.trim().length > 0) {
            try {
              featuresArr = JSON.parse(p.features);
            } catch (_) {
              featuresArr = [];
            }
          }
          return {
            id: String(p.id || idx),
            name: decode(p.name),
            description: decode(p.description || ''),
            originalPrice: Number(p.original_price_monthly || 0),
            discountPrice: Number(p.price_monthly || 0),
            discount:
              Number(p.discount || 0) ||
              (p.original_price_monthly > 0
                ? Math.round(((p.original_price_monthly - p.price_monthly) / p.original_price_monthly) * 100)
                : 0),
            features: featuresArr.map((f) => {
              const rawText = f && f.name ? f.name : String(f);
              return {
                text: decode(rawText),
                included: f && f.included !== false
              };
            }),
            popular: p.is_popular === true || p.is_popular === '1' || p.is_popular === 1
          };
        });
        setPlans(mapped);
        if (mapped.length > 0) setSelectedPlan(mapped[0].id);
      } else {
        setPlans([]);
      }
    } catch (e) {
      setPlansError('Paketler yüklenirken hata oluştu: ' + e.message);
    } finally {
      setLoadingPlans(false);
    }
  };

  const fetchFaqs = async () => {
    try {
      setLoadingFaqs(true);
      const res = await fetch(API_URLS.FAQ);
      const json = await res.json();
      const categories = json.categories || [];
      const questions = json.questions || [];
      const wpCat = categories.find((c) => (c.title || '').toLowerCase() === 'wordpress hosting');
      if (wpCat) {
        const list = questions
          .filter((q) => q.category_id === wpCat.id)
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
          .map((q) => ({ question: q.question, answer: q.answer }));
        setFaqs(list);
      } else {
        setFaqs([]);
      }
    } catch (e) {
      setFaqsError('SSS yüklenirken hata oluştu: ' + e.message);
    } finally {
      setLoadingFaqs(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=getCompanySettings`);
      if (res.ok) {
        const json = await res.json();
        if (json.status === 'success' && json.data?.whatsapp) {
          setWhatsappNumber(json.data.whatsapp);
        }
      }
    } catch (e) {
      console.error('WhatsApp numarası alınamadı:', e);
    }
  };

  const fetchContent = async () => {
    try {
      const res = await fetch(API_URLS.PAGE_CONTENT('wordpress-hosting'));
      const json = await res.json();
      if (json.status === 'success' && json.data) {
        const additionalRaw = json.data.additional_sections;
        let additional = {};
        if (typeof additionalRaw === 'string' && additionalRaw.trim().startsWith('{')) {
          try {
            additional = JSON.parse(additionalRaw);
          } catch (err) {
            console.error('WordPress hosting additional_sections parse error:', err);
            additional = {};
          }
        } else if (additionalRaw && typeof additionalRaw === 'object') {
          additional = additionalRaw;
        }
        setPageContent({
          ...json.data,
          additional_sections: additional
        });
      } else {
        setPageContent(null);
      }
    } catch (error) {
      console.error('WordPress hosting içeriği alınamadı:', error);
      setPageContent(null);
    }
  };

  const derivedContent = useMemo(() => {
    const additionalRaw = pageContent?.additional_sections;
    const additional = additionalRaw && typeof additionalRaw === 'object' ? additionalRaw : {};

    const heroAdditional = additional.hero && typeof additional.hero === 'object' ? additional.hero : {};
    const packagesSection = additional.packages_section && typeof additional.packages_section === 'object'
      ? additional.packages_section
      : {};
    const featuresSection = additional.features_section && typeof additional.features_section === 'object'
      ? additional.features_section
      : {};
    const introSection = additional.intro_section && typeof additional.intro_section === 'object'
      ? additional.intro_section
      : {};
    const technologySection = additional.technology_section && typeof additional.technology_section === 'object'
      ? additional.technology_section
      : {};
    const controlPanelSection = additional.control_panel_section && typeof additional.control_panel_section === 'object'
      ? additional.control_panel_section
      : {};
    const securitySection = additional.security_section && typeof additional.security_section === 'object'
      ? additional.security_section
      : {};
    const supportSection = additional.support_section && typeof additional.support_section === 'object'
      ? additional.support_section
      : {};
    const blogSection = additional.blog_section && typeof additional.blog_section === 'object'
      ? additional.blog_section
      : {};
    const ctaSection = additional.cta_section && typeof additional.cta_section === 'object'
      ? additional.cta_section
      : {};

    return {
      hero: {
        badge: pageContent?.hero_badge_text || '',
        title: pageContent?.hero_title || '',
        highlight: heroAdditional.highlight_text || '',
        description: pageContent?.hero_description || '',
        image_url: heroAdditional.image_url || pageContent?.hero_image_url || '',
        bullets: Array.isArray(heroAdditional.bullets) ? heroAdditional.bullets : []
      },
      packages_section: {
        title: packagesSection.title || '',
        description: packagesSection.description || '',
        note: packagesSection.note || ''
      },
      features_section: {
        title: featuresSection.title || '',
        subtitle: featuresSection.subtitle || '',
        cards: Array.isArray(featuresSection.cards) ? featuresSection.cards : []
      },
      intro_section: {
        title: introSection.title || '',
        description: introSection.description || '',
        image_url: introSection.image_url || ''
      },
      technology_section: {
        title: technologySection.title || '',
        subtitle: technologySection.subtitle || '',
        highlight_title: technologySection.highlight_title || '',
        highlight_description: technologySection.highlight_description || '',
        description: technologySection.description || '',
        image_url: technologySection.image_url || '',
        cards: Array.isArray(technologySection.cards) ? technologySection.cards : []
      },
      control_panel_section: {
        title: controlPanelSection.title || '',
        description: controlPanelSection.description || '',
        image_url: controlPanelSection.image_url || '',
        cards: Array.isArray(controlPanelSection.cards) ? controlPanelSection.cards : []
      },
      security_section: {
        blocks: Array.isArray(securitySection.blocks) ? securitySection.blocks : []
      },
      support_section: {
        title: supportSection.title || '',
        subtitle: supportSection.subtitle || '',
        image_url: supportSection.image_url || '',
        cards: Array.isArray(supportSection.cards) ? supportSection.cards : []
      },
      blog_section: {
        title: blogSection.title || '',
        description: blogSection.description || ''
      },
      cta_section: {
        title: ctaSection.title || '',
        description: ctaSection.description || '',
        primary_button: ctaSection.primary_button || null,
        secondary_button: ctaSection.secondary_button || null,
        highlights: Array.isArray(ctaSection.highlights) ? ctaSection.highlights : []
      }
    };
  }, [pageContent]);

  const heroBullets = useMemo(() => {
    return (derivedContent.hero.bullets || [])
      .map((bullet) => {
        if (typeof bullet === 'string') {
          const text = bullet.trim();
          return text ? { text, icon: 'CheckCircle' } : null;
        }
        if (bullet && typeof bullet === 'object') {
          const text = (bullet.text || bullet.title || '').toString().trim();
          if (!text) return null;
          return { text, icon: bullet.icon || 'CheckCircle' };
        }
        return null;
      })
      .filter(Boolean);
  }, [derivedContent.hero.bullets]);

  const featureCards = useMemo(() => {
    return (derivedContent.features_section.cards || [])
      .map((card) => ({
        icon: card?.icon,
        title: (card?.title || '').toString().trim(),
        description: (card?.description || '').toString().trim()
      }))
      .filter((card) => card.title || card.description || card.icon);
  }, [derivedContent.features_section.cards]);

  const technologyCards = useMemo(() => {
    return (derivedContent.technology_section.cards || [])
      .map((card) => ({
        icon: card?.icon,
        title: (card?.title || '').toString().trim(),
        description: (card?.description || '').toString().trim()
      }))
      .filter((card) => card.title || card.description || card.icon);
  }, [derivedContent.technology_section.cards]);

  const controlPanelCards = useMemo(() => {
    return (derivedContent.control_panel_section.cards || [])
      .map((card) => ({
        icon: card?.icon,
        title: (card?.title || '').toString().trim(),
        subtitle: (card?.subtitle || '').toString().trim(),
        description: (card?.description || '').toString().trim()
      }))
      .filter((card) => card.title || card.subtitle || card.description || card.icon);
  }, [derivedContent.control_panel_section.cards]);

  const securityBlocks = useMemo(() => {
    return (derivedContent.security_section.blocks || [])
      .map((block) => ({
        title: (block?.title || '').toString().trim(),
        subtitle: (block?.subtitle || '').toString().trim(),
        description: (block?.description || '').toString().trim(),
        secondary_description: (block?.secondary_description || '').toString().trim(),
        cards: Array.isArray(block?.cards)
          ? block.cards
              .map((item) => ({
                icon: item?.icon,
                title: (item?.title || '').toString().trim(),
                description: (item?.description || '').toString().trim()
              }))
              .filter((item) => item.title || item.description || item.icon)
          : []
      }))
      .filter((block) => block.title || block.subtitle || block.description || block.secondary_description || block.cards.length);
  }, [derivedContent.security_section.blocks]);

  const supportCards = useMemo(() => {
    return (derivedContent.support_section.cards || [])
      .map((card) => ({
        icon: card?.icon,
        title: (card?.title || '').toString().trim(),
        description: (card?.description || '').toString().trim()
      }))
      .filter((card) => card.title || card.description || card.icon);
  }, [derivedContent.support_section.cards]);

  const ctaHighlights = useMemo(() => {
    return (derivedContent.cta_section.highlights || [])
      .map((highlight) => ({
        icon: highlight?.icon,
        title: (highlight?.title || '').toString().trim(),
        description: (highlight?.description || '').toString().trim()
      }))
      .filter((highlight) => highlight.title || highlight.description || highlight.icon);
  }, [derivedContent.cta_section.highlights]);

  const primaryButton = useMemo(() => {
    const btn = derivedContent.cta_section.primary_button;
    if (!btn || typeof btn !== 'object') return null;
    const text = (btn.text || '').toString().trim();
    const link = (btn.link || '').toString().trim();
    if (!text || !link) return null;
    return { ...btn, text, link };
  }, [derivedContent.cta_section.primary_button]);

  const secondaryButton = useMemo(() => {
    const btn = derivedContent.cta_section.secondary_button;
    if (!btn || typeof btn !== 'object') return null;
    const text = (btn.text || '').toString().trim();
    if (!text) return null;
    const link = (btn.link || '').toString().trim();
    const action = (btn.action || '').toString().trim();
    const phone = (btn.phone || '').toString().trim();
    const prefill_message = (btn.prefill_message || '').toString();
    return { ...btn, text, link, action, phone, prefill_message };
  }, [derivedContent.cta_section.secondary_button]);

  const showPrimaryButton = Boolean(primaryButton);
  const showSecondaryButton = Boolean(secondaryButton);

  const hasHeroContent = Boolean(
    derivedContent.hero.badge ||
      derivedContent.hero.title ||
      derivedContent.hero.highlight ||
      derivedContent.hero.description ||
      derivedContent.hero.image_url ||
      heroBullets.length
  );

  const showPackagesHeader = Boolean(
    derivedContent.packages_section.title || derivedContent.packages_section.description
  );

  const hasFeaturesSection = Boolean(
    derivedContent.features_section.title ||
      derivedContent.features_section.subtitle ||
      featureCards.length
  );

  const hasIntroSection = Boolean(
    derivedContent.intro_section.title ||
      derivedContent.intro_section.description ||
      derivedContent.intro_section.image_url
  );

  const hasTechnologySection = Boolean(
    derivedContent.technology_section.title ||
      derivedContent.technology_section.subtitle ||
      derivedContent.technology_section.highlight_title ||
      derivedContent.technology_section.highlight_description ||
      derivedContent.technology_section.description ||
      derivedContent.technology_section.image_url ||
      technologyCards.length
  );

  const hasControlPanelSection = Boolean(
    derivedContent.control_panel_section.title ||
      derivedContent.control_panel_section.description ||
      derivedContent.control_panel_section.image_url ||
      controlPanelCards.length
  );

  const hasSecuritySection = securityBlocks.length > 0;
  const hasSupportSection = Boolean(
    derivedContent.support_section.title ||
      derivedContent.support_section.subtitle ||
      derivedContent.support_section.image_url ||
      supportCards.length
  );

  const hasBlogSection = Boolean(
    derivedContent.blog_section.title || derivedContent.blog_section.description
  );

  const hasCTASection = Boolean(
    derivedContent.cta_section.title ||
      derivedContent.cta_section.description ||
      showPrimaryButton ||
      showSecondaryButton ||
      ctaHighlights.length
  );

  const securityPrimary = securityBlocks[0] || null;
  const securitySecondary = securityBlocks[1] || null;
  const remainingSecurityBlocks = securityBlocks.slice(2);

  const decode = (str) => {
    if (!str) return str;
    return str
      .replace(/Ä±/g, 'ı')
      .replace(/Ä°/g, 'İ')
      .replace(/ÄŸ/g, 'ğ')
      .replace(/Ã¼/g, 'ü')
      .replace(/Ã§/g, 'ç')
      .replace(/Ã¶/g, 'ö')
      .replace(/Ã‡/g, 'Ç')
      .replace(/Ã–/g, 'Ö')
      .replace(/Ãœ/g, 'Ü')
      .replace(/ÅŸ/g, 'Ş')
      .replace(/ÄŸ/g, 'Ğ')
      .replace(/yÄ±l/g, 'yıl');
  };

  const PackageCard = ({ plan, index }) => {
    const isExpanded = expandedFeatures[plan.id];

    return (
      <Card className={`relative border-0 shadow-xl cursor-pointer ${plan.popular ? 'ring-4 ring-emerald-200' : ''} bg-white`}>
        {plan.popular && (
          <div className="absolute top-0 transform -translate-x-1/2 -translate-y-1/2 z-10" style={{ left: '52%' }}>
            <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-1 text-sm font-bold" style={{ width: '150px' }}>
              <Crown className="w-4 h-4 mr-1" />
              EN POPÜLER
            </Badge>
          </div>
        )}

        <CardHeader className="space-y-4 pb-6">
          <div className="space-y-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-600 to-green-700 flex items-center justify-center">
              <Server className="w-8 h-8 text-white" />
            </div>

            <div className="space-y-2">
              <CardTitle className="text-2xl font-bold text-gray-900">{plan.name}</CardTitle>
              {plan.description && (
                <p className="text-gray-600 leading-relaxed">{plan.description}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-baseline space-x-2">
              <span className="text-4xl font-bold text-gray-900">₺{plan.discountPrice.toLocaleString()}</span>
              <span className="text-gray-600">/yıllık</span>
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-lg text-gray-400 line-through">₺{plan.originalPrice.toLocaleString()}</span>
              <Badge variant="destructive">%{plan.discount} İndirim</Badge>
            </div>

          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-800">Temel Özellikler:</h4>
            {plan.features.slice(0, 5).map((feature, idx) => {
              const isIncluded = feature.included !== false;
              const featureText = typeof feature === 'string' ? feature : feature?.text || feature?.name || String(feature);
              return (
                <div key={idx} className="flex items-center space-x-3">
                  {isIncluded ? (
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  ) : (
                    <XIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                  <span className={`text-sm font-medium ${isIncluded ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                    {featureText}
                  </span>
                </div>
              );
            })}

            {plan.features.length > 5 && (
              <div>
                <button
                  onClick={() => toggleFeatures(plan.id)}
                  className="flex items-center justify-center w-full text-sm text-blue-600 hover:text-blue-800 transition-colors mt-4"
                >
                  {isExpanded ? (
                    <>
                      <Icons.ChevronUp className="w-4 h-4 mr-1" />
                      Daha Az Detay
                    </>
                  ) : (
                    <>
                      <Icons.ChevronDown className="w-4 h-4 mr-1" />
                      Daha Fazla Detay
                    </>
                  )}
                </button>

                {isExpanded && (
                  <div className="space-y-3 mt-4 pt-4 border-t border-gray-200">
                    {plan.features.slice(5).map((feature, idx) => {
                      const isIncluded = feature.included !== false;
                      const featureText = typeof feature === 'string' ? feature : feature?.text || feature?.name || String(feature);
                      return (
                        <div key={idx} className="flex items-center space-x-3">
                          {isIncluded ? (
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                          ) : (
                            <XIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                          )}
                          <span className={`text-sm font-medium ${isIncluded ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                            {featureText}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          <Button
            className="w-full h-12 font-semibold bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white"
            onClick={() => setSelectedPlan(plan.id)}
          >
            <span>PLANI SEÇ</span>
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </CardContent>
      </Card>
    );
  };

  const securityCard = (item, idx) => {
    const ItemIcon = resolveIcon(item.icon, 'Shield');
    return (
      <div key={idx} className="bg-white rounded-xl p-6 shadow-sm">
        <div className={`w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-500 rounded-full flex items-center justify-center mb-4 text-white`}>
          <ItemIcon className="w-6 h-6" />
        </div>
        {item.title && <h4 className="font-semibold text-gray-900 mb-2">{item.title}</h4>}
        {item.description && <p className="text-gray-600 text-sm">{item.description}</p>}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {hasHeroContent && (
        <section
          className="relative bg-gradient-to-br from-emerald-50 via-white to-green-50 py-20 overflow-hidden"
          style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.6s ease-in' }}
        >
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-20 left-10 w-64 h-64 bg-emerald-200 rounded-full opacity-20 blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-green-300 rounded-full opacity-15 blur-3xl"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8 text-center lg:text-left">
                <div className="space-y-6">
                  {derivedContent.hero.badge && (
                    <Badge className="bg-emerald-600 text-white px-4 py-2 text-lg mx-auto lg:mx-0">
                      {derivedContent.hero.badge}
                    </Badge>
                  )}
                  {(derivedContent.hero.title || derivedContent.hero.highlight) && (
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                      {derivedContent.hero.title && (
                        <span className="text-gray-900 block">{derivedContent.hero.title}</span>
                      )}
                      {derivedContent.hero.highlight && (
                        <span className="bg-gradient-to-r from-emerald-600 to-green-700 bg-clip-text text-transparent">
                          {derivedContent.hero.highlight}
                        </span>
                      )}
                    </h1>
                  )}
                  {derivedContent.hero.description && (
                    <p className="text-xl text-gray-600 leading-relaxed">{derivedContent.hero.description}</p>
                  )}
                </div>

                {heroBullets.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 justify-items-center lg:justify-items-start">
                    {heroBullets.map((bullet, idx) => {
                      const BulletIcon = resolveIcon(bullet.icon, 'CheckCircle');
                      return (
                        <div key={idx} className="flex items-center space-x-3">
                          <BulletIcon className="w-6 h-6 text-emerald-600" />
                          <span className="text-lg font-semibold text-gray-900">{bullet.text}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {derivedContent.hero.image_url && (
                <div className="relative hidden lg:block">
                  <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
                    <div className="text-center space-y-6">
                      <MediaDisplay
                        src={derivedContent.hero.image_url}
                        alt={derivedContent.hero.title || 'WordPress Hosting'}
                        className="w-full max-w-md mx-auto rounded-lg"
                        videoProps={{ controls: true }}
                        imgProps={{ loading: "lazy" }}
                      />
                      <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <Shield className="w-4 h-4 mr-1 text-emerald-500" />
                          <span>Güvenli</span>
                        </div>
                        <div className="flex items-center">
                          <Zap className="w-4 h-4 mr-1 text-yellow-500" />
                          <span>Hızlı</span>
                        </div>
                        <div className="flex items-center">
                          <Star className="w-4 h-4 mr-1 text-emerald-500" />
                          <span>Güvenilir</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      <section
        className="py-20 bg-white"
        style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.6s ease-in' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {showPackagesHeader && (
            <div className="text-center mb-16">
              {derivedContent.packages_section.title && (
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  {derivedContent.packages_section.title}
                </h2>
              )}
              {derivedContent.packages_section.description && (
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  {derivedContent.packages_section.description}
                </p>
              )}
            </div>
          )}

          {plansError && <div className="mb-6 text-red-600 text-center">{plansError}</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {(!loadingPlans ? plans.slice(0, 4) : []).map((plan, index) => (
              <PackageCard key={plan.id} plan={plan} index={index} />
            ))}
          </div>
          {loadingPlans && <div className="text-center text-gray-500">Paketler yükleniyor...</div>}
          {derivedContent.packages_section.note && (
            <div className="text-right text-sm text-gray-500 mt-4">
              {derivedContent.packages_section.note}
            </div>
          )}
        </div>
      </section>

      {hasFeaturesSection && (
        <section
          className="py-20 bg-gray-50"
          style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.6s ease-in' }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              {derivedContent.features_section.title && (
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  {derivedContent.features_section.title}
                </h2>
              )}
              {derivedContent.features_section.subtitle && (
                <p className="text-xl text-gray-600">
                  {derivedContent.features_section.subtitle}
                </p>
              )}
            </div>

            {featureCards.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {featureCards.map((feature, index) => {
                  const FeatureIcon = resolveIcon(feature.icon, 'Settings');
                  return (
                    <Card key={index} className="text-center hover:shadow-lg transition-shadow duration-300">
                      <CardContent className="p-8">
                        <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                          <FeatureIcon className="w-8 h-8 text-white" />
                        </div>
                        {feature.title && (
                          <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                        )}
                        {feature.description && (
                          <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {hasIntroSection && (
        <section
          className="py-20 bg-white"
          style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.6s ease-in' }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center flex-col-reverse lg:grid">
              {derivedContent.intro_section.image_url && (
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg overflow-hidden">
                  <MediaDisplay
                    src={derivedContent.intro_section.image_url}
                    alt={derivedContent.intro_section.title || 'WordPress Hosting'}
                    className="w-full h-full object-cover"
                    videoProps={{ controls: true }}
                    imgProps={{ loading: "lazy" }}
                  />
                </div>
              )}
              <div className="text-center lg:text-left">
                {derivedContent.intro_section.title && (
                  <h2 className="text-4xl font-bold text-gray-900 mb-6">
                    {derivedContent.intro_section.title}
                  </h2>
                )}
                {derivedContent.intro_section.description && (
                  <p className="text-xl text-gray-600 leading-relaxed">
                    {derivedContent.intro_section.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {hasTechnologySection && (
        <section
          className="py-20 bg-gray-50"
          style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.6s ease-in' }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              {derivedContent.technology_section.title && (
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  {derivedContent.technology_section.title}
                </h2>
              )}
              {derivedContent.technology_section.subtitle && (
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  {derivedContent.technology_section.subtitle}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16 flex-col-reverse lg:grid">
              {derivedContent.technology_section.image_url && (
                <div className="bg-white rounded-xl shadow-lg p-8">
                  <MediaDisplay
                    src={derivedContent.technology_section.image_url}
                    alt={derivedContent.technology_section.title || 'JetPress'}
                    className="w-full rounded-lg"
                    videoProps={{ controls: true }}
                    imgProps={{ loading: "lazy" }}
                  />
                </div>
              )}
              <div className="space-y-6 text-center lg:text-left">
                {derivedContent.technology_section.highlight_title && (
                  <h3 className="text-3xl font-bold text-gray-900">
                    {derivedContent.technology_section.highlight_title}
                  </h3>
                )}
                {derivedContent.technology_section.highlight_description && (
                  <p className="text-lg text-gray-600 leading-relaxed">
                    {derivedContent.technology_section.highlight_description}
                  </p>
                )}
              </div>
            </div>

            {derivedContent.technology_section.description && (
              <p className="text-lg text-gray-600 leading-relaxed mb-12 max-w-4xl mx-auto text-center lg:text-left">
                {derivedContent.technology_section.description}
              </p>
            )}

            {technologyCards.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {technologyCards.map((card, idx) => {
                  const CardIcon = resolveIcon(card.icon, 'Zap');
                  return (
                    <Card key={idx} className="hover:shadow-lg transition-shadow duration-300">
                      <CardContent className="p-8">
                        <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-green-500 rounded-full flex items-center justify-center mb-6">
                          <CardIcon className="w-8 h-8 text-white" />
                        </div>
                        {card.title && (
                          <h4 className="text-xl font-bold text-gray-900 mb-4">{card.title}</h4>
                        )}
                        {card.description && (
                          <p className="text-gray-600 leading-relaxed">{card.description}</p>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {hasControlPanelSection && (
        <section
          className="py-20 bg-white"
          style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.6s ease-in' }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              {derivedContent.control_panel_section.title && (
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  {derivedContent.control_panel_section.title}
                </h2>
              )}
              {derivedContent.control_panel_section.description && (
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  {derivedContent.control_panel_section.description}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {controlPanelCards.length > 0 && (
                <div className="space-y-6 text-center lg:text-left">
                  {controlPanelCards.map((card, idx) => {
                    const CardIcon = resolveIcon(card.icon, idx === 0 ? 'Shield' : idx === 1 ? 'Settings' : 'Database');
                    return (
                      <div key={idx} className="flex flex-col lg:flex-row items-center lg:items-start space-y-3 lg:space-y-0 lg:space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                          <CardIcon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          {card.title && (
                            <h4 className="text-xl font-bold text-gray-900 mb-2">{card.title}</h4>
                          )}
                          {card.subtitle && (
                            <p className="text-sm text-gray-600 mb-2">{card.subtitle}</p>
                          )}
                          {card.description && (
                            <p className="text-gray-600">{card.description}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {derivedContent.control_panel_section.image_url && (
                <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl p-8 hidden lg:block">
                  <MediaDisplay
                    src={derivedContent.control_panel_section.image_url}
                    alt={derivedContent.control_panel_section.title || 'Kontrol Paneli'}
                    className="w-full rounded-lg shadow-lg"
                    videoProps={{ controls: true }}
                    imgProps={{ loading: "lazy" }}
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {hasSecuritySection && (
        <section
          className="py-20 bg-gray-50"
          style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.6s ease-in' }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {securityPrimary && (
                <div className="space-y-8 text-center lg:text-left">
                  {securityPrimary.title && (
                    <h3 className="text-3xl font-bold text-gray-900">{securityPrimary.title}</h3>
                  )}
                  {securityPrimary.description && (
                    <p className="text-lg text-gray-600 leading-relaxed">
                      {securityPrimary.description}
                    </p>
                  )}
                  {securityPrimary.secondary_description && (
                    <p className="text-gray-600 leading-relaxed">
                      {securityPrimary.secondary_description}
                    </p>
                  )}
                  {securityPrimary.cards && securityPrimary.cards.length > 0 && (
                    <div className="grid grid-cols-1 gap-4">
                      {securityPrimary.cards.map((card, idx) => securityCard(card, idx))}
                    </div>
                  )}
                </div>
              )}

              {securitySecondary && (
                <div className="space-y-8 text-center lg:text-left">
                  {securitySecondary.title && (
                    <h3 className="text-3xl font-bold text-gray-900">{securitySecondary.title}</h3>
                  )}
                  {securitySecondary.description && (
                    <p className="text-lg text-gray-600 leading-relaxed">
                      {securitySecondary.description}
                    </p>
                  )}
                  {securitySecondary.secondary_description && (
                    <p className="text-gray-600 leading-relaxed">
                      {securitySecondary.secondary_description}
                    </p>
                  )}
                  {securitySecondary.cards && securitySecondary.cards.length > 0 && (
                    <div className="grid grid-cols-1 gap-4">
                      {securitySecondary.cards.map((card, idx) => securityCard(card, idx))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {remainingSecurityBlocks.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {remainingSecurityBlocks.map((block, idx) => (
                  <div key={idx} className="bg-white rounded-xl p-6 shadow-sm space-y-4">
                    {block.title && (
                      <h4 className="text-2xl font-semibold text-gray-900">{block.title}</h4>
                    )}
                    {block.description && (
                      <p className="text-gray-600 leading-relaxed">{block.description}</p>
                    )}
                    {block.secondary_description && (
                      <p className="text-gray-500 text-sm">{block.secondary_description}</p>
                    )}
                    {block.cards && block.cards.length > 0 && (
                      <div className="grid grid-cols-1 gap-4">
                        {block.cards.map((card, cardIdx) => securityCard(card, cardIdx))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {hasSupportSection && (
        <section
          className="py-20 bg-gray-50"
          style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.6s ease-in' }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {derivedContent.support_section.image_url && (
                <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg overflow-hidden">
                  <MediaDisplay
                    src={derivedContent.support_section.image_url}
                    alt={derivedContent.support_section.title || 'Destek'}
                    className="w-full h-full object-cover"
                    videoProps={{ controls: true }}
                    imgProps={{ loading: "lazy" }}
                  />
                </div>
              )}

              <div className="text-center lg:text-left">
                {derivedContent.support_section.title && (
                  <h2 className="text-4xl font-bold text-gray-900 mb-4">
                    {derivedContent.support_section.title}
                  </h2>
                )}
                {derivedContent.support_section.subtitle && (
                  <p className="text-xl text-gray-600 mb-8">
                    {derivedContent.support_section.subtitle}
                  </p>
                )}

                {supportCards.length > 0 && (
                  <div className="space-y-4">
                    {supportCards.map((card, idx) => {
                      const SupportIcon = resolveIcon(card.icon, 'Settings');
                      return (
                        <div key={idx} className="bg-white rounded-xl border border-gray-200 p-6">
                          <div className="flex flex-col lg:flex-row items-center lg:items-start gap-4 text-center lg:text-left">
                            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                              <SupportIcon className="w-6 h-6 text-white" />
                            </div>
                            <div>
                              {card.title && (
                                <h4 className="font-semibold text-gray-900 mb-1">{card.title}</h4>
                              )}
                              {card.description && <p className="text-gray-600">{card.description}</p>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {hasBlogSection && (
        <section
          className="py-20 bg-gray-50"
          style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.6s ease-in' }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              {derivedContent.blog_section.title && (
                <h3 className="text-2xl font-bold text-gray-900">
                  {derivedContent.blog_section.title}
                </h3>
              )}
              {derivedContent.blog_section.description && (
                <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                  {derivedContent.blog_section.description}
                </p>
              )}
            </div>
            <BlogTeasersWpHosting />
          </div>
        </section>
      )}

      <section
        className="py-16 bg-gray-50"
        style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.6s ease-in' }}
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              Sıkça Sorulan <span className="text-emerald-600">Sorular</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              WordPress Hosting ile ilgili merak ettiğiniz soruların cevaplarını burada bulabilirsiniz
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            {loadingFaqs ? (
              <div className="text-center p-8 bg-white rounded-xl">
                <p className="text-gray-500">Yükleniyor...</p>
              </div>
            ) : faqs.length === 0 ? (
              <div className="text-center p-8 bg-white rounded-xl">
                <p className="text-gray-500">{faqsError || 'Bu kategori için soru bulunamadı.'}</p>
              </div>
            ) : (
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">
                      <div className="flex items-center">
                        <HelpCircle className="w-5 h-5 mr-3 text-emerald-600" />
                        {faq.question}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600">{faq.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        </div>
      </section>

      {hasCTASection && (
        <section
          className="py-20 bg-gradient-to-r from-emerald-600 to-green-600 text-white"
          style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.6s ease-in' }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              {derivedContent.cta_section.title && (
                <h2 className="text-4xl font-bold mb-4">{derivedContent.cta_section.title}</h2>
              )}
              {derivedContent.cta_section.description && (
                <p className="text-xl text-emerald-100 mb-8 max-w-3xl mx-auto">
                  {derivedContent.cta_section.description}
                </p>
              )}
              {(showPrimaryButton || showSecondaryButton) && (
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  {showPrimaryButton && primaryButton && (() => {
                    const PrimaryIcon = resolveIcon(primaryButton.icon, 'Mail');
                    const link = primaryButton.link;
                    return (
                      <a
                        href={link.startsWith('/') ? link : link}
                        key="primary"
                        target={link.startsWith('/') ? undefined : '_blank'}
                        rel={link.startsWith('/') ? undefined : 'noopener noreferrer'}
                        className="inline-flex items-center px-8 py-4 bg-white text-emerald-600 rounded-lg hover:bg-gray-100 font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
                      >
                        <PrimaryIcon className="w-5 h-5 mr-2" />
                        {primaryButton.text}
                      </a>
                    );
                  })()}

                  {showSecondaryButton && secondaryButton && (() => {
                    const SecondaryIcon = resolveIcon(secondaryButton.icon, 'MessageCircle');
                    const isWhatsapp = secondaryButton.action === 'whatsapp';
                    const phone = secondaryButton.phone || whatsappNumber;
                    const message = secondaryButton.prefill_message || '';
                    const link = isWhatsapp
                      ? `https://api.whatsapp.com/send/?phone=${encodeURIComponent(phone)}${
                          message ? `&text=${encodeURIComponent(message)}` : ''
                        }`
                      : secondaryButton.link;
                    return (
                      <a
                        key="secondary"
                        href={link || '#'}
                        target={isWhatsapp || (link && !link.startsWith('/')) ? '_blank' : undefined}
                        rel={isWhatsapp || (link && !link.startsWith('/')) ? 'noopener noreferrer' : undefined}
                        className="inline-flex items-center px-8 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold text-lg transition-all duration-300 transform hover:scale-105 border-2 border-white shadow-lg"
                      >
                        <SecondaryIcon className="w-5 h-5 mr-2" />
                        {secondaryButton.text}
                      </a>
                    );
                  })()}
                </div>
              )}
              {ctaHighlights.length > 0 && (
                <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
                  {ctaHighlights.map((highlight, idx) => {
                    const HighlightIcon = resolveIcon(highlight.icon, 'Clock');
                    return (
                      <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center space-y-2">
                        <HighlightIcon className="w-12 h-12 mx-auto mb-4" />
                        {highlight.title && <h3 className="font-semibold text-lg">{highlight.title}</h3>}
                        {highlight.description && <p className="text-emerald-100">{highlight.description}</p>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default WordPressHostingPage;

const BlogTeasersWpHosting = () => {
  const [posts, setPosts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  const ADMIN_API = API_URLS.ADMIN?.ADMIN_API || '';
  const BASE_ORIGIN = React.useMemo(() => {
    try {
      if (ADMIN_API) {
        const u = new URL(ADMIN_API);
        return `${u.protocol}//${u.host}`;
      }
      return window.location.origin;
    } catch (_) {
      return window.location.origin;
    }
  }, [ADMIN_API]);

  const normalizeImageUrl = (url) => {
    if (!url) return '';
    try {
      const str = String(url);
      if (str.includes('getScreenshot') && str.includes('file=')) {
        const parsed = new URL(str, ADMIN_API || BASE_ORIGIN);
        const file = parsed.searchParams.get('file');
        if (file) return `${BASE_ORIGIN}/uploads/${file}`;
      }
      if (str.startsWith('http')) return str;
      if (str.startsWith('/uploads/')) return `${BASE_ORIGIN}${str}`;
      if (str.includes('/uploads/')) return `${BASE_ORIGIN}${str.startsWith('/') ? '' : '/'}${str}`;
      return `${BASE_ORIGIN}/uploads/${str.replace(/^\/+/, '')}`;
    } catch (_) {
      return url;
    }
  };

  React.useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        // Try server-side filtered fetch first
        const res = await fetch(`${API_URLS.BLOGS}&category=wordpress-hosting&limit=3`);
        const json = await res.json();
        let arr = Array.isArray(json) ? json : (json.blogs || json.data || []);
        
        // Fallback: fetch all then client-side filter if empty
        if (!arr || arr.length === 0) {
          const allRes = await fetch(API_URLS.BLOGS);
          const allJson = await allRes.json();
          const allArr = Array.isArray(allJson) ? allJson : (allJson.blogs || allJson.data || []);
          arr = allArr
            .filter((p) => {
              const raw = (p.category || p.category_slug || p.category_name || '').toString().toLowerCase();
              const hyphenNorm = raw.replace(/[\-_]/g, ' ');
              return raw.includes('wordpress-hosting') || hyphenNorm.includes('wordpress hosting');
            })
            .slice(0, 3);
        }
        
        setPosts(arr);
      } catch (e) {
        setError('Bloglar yüklenirken hata: ' + e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  if (loading) return <div className="text-center text-gray-500">İçerikler yükleniyor...</div>;
  if (error) return <div className="text-center text-red-600">{error}</div>;
  if (!posts.length) return <div className="text-center text-gray-500">Henüz WordPress Hosting kategorisinde içerik bulunmuyor.</div>;

  return (
    <>
      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
        {posts.map((post) => {
          const hasValidImage = post.image && 
            post.image !== 'https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?auto=format&fit=crop&w=800&q=80' &&
            !post.image.includes('placeholder');
          
          return (
            <Card key={post.id} className="overflow-hidden border-emerald-100 hover:shadow-lg transition-shadow">
              {hasValidImage ? (
                <div className="aspect-[16/9] bg-emerald-50 overflow-hidden">
                  <MediaDisplay
                    src={normalizeImageUrl(post.image)}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                    videoProps={{ controls: true }}
                    imgProps={{
                      loading: "lazy",
                      onError: (e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="w-full h-full bg-gradient-to-br from-emerald-500 to-green-700 flex items-center justify-center"><svg class="w-16 h-16 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="aspect-[16/9] bg-gradient-to-br from-emerald-500 to-green-700 flex items-center justify-center">
                  <svg className="w-16 h-16 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                </div>
              )}
              <CardContent className="p-5 space-y-3">
                <div className="inline-flex items-center px-3 py-1 bg-emerald-50 text-emerald-600 text-xs font-semibold rounded-full">
                  <BookOpen className="w-3.5 h-3.5 mr-1" />
                  WordPress Hosting
                </div>
                <h3 className="text-lg font-semibold text-gray-900">{post.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{post.excerpt || ''}</p>
                <button
                  onClick={() => window.open(`/blog/${post.slug || post.id}`, '_self')}
                  className="inline-flex items-center text-sm font-semibold text-emerald-600 hover:text-emerald-700"
                >
                  Yazıyı Oku
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </>
  );
};
